import { Box, Paper, Stack, Typography } from '@mui/material';
import type { CustomerPortalInvoicesSummary } from '@vy/api-definition';
import type { CustomerPortalCustomer, CustomerPortalInvoice } from '@vy/types';
import { INVOICE_BILLING_STATUSES } from '@vy/types';
import type { Nullable } from '@vy/utils';
import { isNotNullOrUndefined } from '@vy/utils';
import { useState } from 'react';
import { VyTable } from '../../../../../components/new-design/table/vy-table/VyTable';
import { CUSTOMER_PORTAL_PAGE_SIZE } from '../../../../../hooks/requests/use-customer-portal/use-get-customer-portal-consts';
import { usePageParam } from '../../../../../hooks/search-params/use-page-param';
import { usePageSizeParam } from '../../../../../hooks/search-params/use-page-size-param';
import { useToggle } from '../../../../../hooks/use-toggle';
import { CustomerPortalInvoiceViewer } from '../CustomerPortalInvoiceViewer/CustomerPortalInvoiceViewer';
import {
  CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS,
  CUSTOMER_PORTAL_INVOICES_ROW_ACTIONS_CLASSNAME,
  CUSTOMER_PORTAL_INVOICES_TABLE_COLUMNS,
} from './customer-portal-invoices-table-const/customer-portal-invoices-columns';
import { VyDivider } from '../../../../../components/new-design/atoms';

type CustomerPortalInvoicesProps = {
  invoices: CustomerPortalInvoice[];
  customer: CustomerPortalCustomer;
  token: string;
  totalInvoicesCount: number;
  invoicesSummary?: CustomerPortalInvoicesSummary;
  isFetching?: boolean;
};

export const CustomerPortalInvoicesTable: React.FC<CustomerPortalInvoicesProps> = ({
  invoices,
  customer,
  token,
  totalInvoicesCount,
  invoicesSummary,
  isFetching = false,
}) => {
  const [drawerOpen, toggleDrawer] = useToggle(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Nullable<CustomerPortalInvoice>>(null);
  const [page, setPage] = usePageParam();
  const [pageSize, setPageSize] = usePageSizeParam(CUSTOMER_PORTAL_PAGE_SIZE);

  const handleViewInvoice = (invoice: CustomerPortalInvoice) => {
    setSelectedInvoice(invoice);
    toggleDrawer();
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        sx={{ padding: '24px 0', flexShrink: 0, alignItems: 'center' }}
        divider={<VyDivider orientation="vertical" flexItem />}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {totalInvoicesCount}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Total Invoices
          </Typography>
        </Stack>
        <InvoiceStatusCount
          count={invoicesSummary?.[INVOICE_BILLING_STATUSES.Overdue] ?? 0}
          label="overdue"
          countColor="#EE2049"
        />
        <InvoiceStatusCount
          count={invoicesSummary?.[INVOICE_BILLING_STATUSES.Paid] ?? 0}
          label="paid"
          countColor="#42AE5C"
        />
        <InvoiceStatusCount
          count={invoicesSummary?.[INVOICE_BILLING_STATUSES.PendingPayment] ?? 0}
          label="pending"
          countColor="#7E4CFF"
        />
      </Stack>
      <Paper
        sx={{
          borderRadius: '10px',
          height: '550px',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <VyTable
            tableName="customer-portal-invoices"
            items={invoices.map((invoice) => ({
              id: invoice.id,
              invoice,
              token,
              onViewInvoice: handleViewInvoice,
            }))}
            itemsCount={totalInvoicesCount}
            columns={CUSTOMER_PORTAL_INVOICES_TABLE_COLUMNS}
            rowHeight={64}
            page={page ?? 0}
            pageSize={pageSize ?? CUSTOMER_PORTAL_PAGE_SIZE}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            getRowId={(row) => row.id}
            loadingItems={isFetching}
            sortingMode="client"
            sx={{
              '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader, & .MuiDataGrid-pinnedColumnHeaders':
                {
                  backgroundColor: '#F5F6FA',
                },
              '& .MuiDataGrid-cell[data-field="billingStatus"]': {
                borderLeft: 'none',
              },
              [`& .${CUSTOMER_PORTAL_INVOICES_ROW_ACTIONS_CLASSNAME}`]: {
                paddingLeft: 0,
                paddingRight: '8px',
                justifyContent: 'flex-end',
              },
              [`& .${CUSTOMER_PORTAL_INVOICES_ROW_ACTIONS_CLASSNAME} > *`]: {
                opacity: 0,
                transition: 'opacity 120ms ease-in-out',
              },
              [`& .MuiDataGrid-row:hover .${CUSTOMER_PORTAL_INVOICES_ROW_ACTIONS_CLASSNAME} > *`]: {
                opacity: 1,
              },
            }}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: CUSTOMER_PORTAL_INVOICES_COLUMNS_KEYS.ISSUED_AT,
                    sort: 'asc',
                  },
                ],
              },
            }}
          />
        </Box>
      </Paper>

      {isNotNullOrUndefined(selectedInvoice) && (
        <CustomerPortalInvoiceViewer
          selectedInvoice={selectedInvoice}
          drawerOpen={drawerOpen}
          toggleDrawer={toggleDrawer}
          customer={customer}
          token={token}
        />
      )}
    </>
  );
};

function InvoiceStatusCount({
  count,
  label,
  countColor,
}: {
  count: number;
  label: string;
  countColor?: string;
}) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Typography variant="body1" sx={{ color: countColor }}>
        {count}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}
